import { api, type AdminDappRequest, queryKeys } from '@alpha/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { DataTable } from '../../components/DataTable';
import { PageHeader } from '../../components/PageHeader';
import { useTableQuery } from '../../hooks/useTableQuery';
import { formatDateTime, formatLds, statusTone } from '../../lib/format';
import { Chip } from '../../ui';

const STATUSES = ['pending', 'confirmed', 'rejected', 'all'] as const;
type Status = (typeof STATUSES)[number];

export const DAPP_REQUEST_TABS = [
  { label: 'Pending', to: '/dapp-requests/pending' },
  { label: 'Confirmed', to: '/dapp-requests/confirmed' },
  { label: 'Rejected', to: '/dapp-requests/rejected' },
  { label: 'All', to: '/dapp-requests/all' },
];

const col = createColumnHelper<AdminDappRequest>();

/**
 * Admin visibility into wallet (DApp) registration requests — client
 * requirement: "ALSO SHOW ALL NECESSARY REGISTRATION DAPP REQUESTS IN ADMIN
 * AS WELL AS USER PANEL". "Recheck" re-runs the on-chain verification
 * (useful if the first pass hit an RPC hiccup, or a slow tx has since
 * confirmed); "Reject" is a manual override for suspected fraud.
 */
export function DappRequests() {
  const { status: raw = 'pending' } = useParams<{ status: Status }>();
  const status = ((STATUSES as readonly string[]).includes(raw) ? raw : 'pending') as Status;
  const { query, setPage } = useTableQuery();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.dappRequests({ status, ...query }),
    queryFn: () => api.admin.dappRequests(status, query),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['dapp-requests'] });
  const recheck = useMutation({ mutationFn: (id: string) => api.admin.recheckDappRequest(id), onSuccess: invalidate });
  const reject = useMutation({
    mutationFn: (id: string) => api.admin.rejectDappRequest(id, 'Rejected by admin'),
    onSuccess: invalidate,
  });

  const columns = useMemo(
    () => [
      col.accessor('wallet_address', {
        header: 'Wallet',
        cell: (i) => `${i.getValue().slice(0, 8)}…${i.getValue().slice(-6)}`,
      }),
      col.accessor('user_code', { header: 'User ID', cell: (i) => i.getValue() ?? '—' }),
      col.accessor('sponsor_code', { header: 'Sponsor', cell: (i) => i.getValue() ?? '—' }),
      col.accessor('tx_hash', {
        header: 'Tx Hash',
        cell: (i) => (i.getValue() ? `${i.getValue()!.slice(0, 10)}…` : '—'),
      }),
      col.accessor('amount_lds', { header: 'LDS', cell: (i) => formatLds(i.getValue()) }),
      col.accessor('status', {
        header: 'Status',
        cell: (i) => <Chip tone={statusTone(i.getValue())}>{i.getValue()}</Chip>,
      }),
      col.accessor('reject_reason', {
        header: 'Reason',
        cell: (i) => <span className="text-muted small">{i.getValue() ?? '—'}</span>,
      }),
      col.accessor('created_at', { header: 'Submitted', cell: (i) => formatDateTime(i.getValue()) }),
      col.display({
        id: 'actions',
        header: 'Actions',
        cell: (info) => {
          const row = info.row.original;
          if (row.status === 'confirmed') return '—';
          return (
            <span className="flex gap-1">
              <Chip as="button" tone="info" onClick={() => recheck.mutate(row.id)}>
                Recheck
              </Chip>
              {row.status !== 'rejected' ? (
                <Chip as="button" tone="danger" onClick={() => reject.mutate(row.id)}>
                  Reject
                </Chip>
              ) : null}
            </span>
          );
        },
      }),
    ],
    [recheck, reject],
  );

  return (
    <>
      <PageHeader
        title="DApp Requests"
        description="Wallet registration requests and their on-chain verification status."
        tabs={DAPP_REQUEST_TABS}
      />
      <DataTable<AdminDappRequest>
        title={`${status[0].toUpperCase()}${status.slice(1)} DApp Requests`}
        columns={columns}
        data={data?.data}
        meta={data?.meta}
        isLoading={isLoading}
        error={error as Error | null}
        onPageChange={setPage}
      />
    </>
  );
}
